import React, { useState, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { useMouseParallax } from "../../utils/mouseParallax";
import { motion } from "framer-motion";

const metrics = [
    {
        id: 1,
        value: "92%",
        label: "Taux de fidélisation client",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
        ),
    },
    {
        id: 2,
        value: "127%",
        label: "ROI moyen pour nos clients",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
            </svg>
        ),
    },
    {
        id: 3,
        value: "48+",
        label: "Sites en première page Google",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                />
            </svg>
        ),
    },
    {
        id: 4,
        value: "3.1s",
        label: "Temps de chargement moyen",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
        ),
    },
];

// Composant pour animer le compteur
const CountUp = ({ end, duration = 2000, suffix = "" }) => {
    const [count, setCount] = useState(0);
    const [inViewRef, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    useEffect(() => {
        let startValue = 0;
        let endValue = parseFloat(end);
        let timer;

        // Ne pas animer si ce n'est pas un nombre
        if (isNaN(endValue)) {
            setCount(end);
            return;
        }

        const startTime = Date.now();

        const updateCounter = () => {
            const currentTime = Date.now();
            const elapsedTime = currentTime - startTime;
            const progress = Math.min(elapsedTime / duration, 1);

            const currentValue =
                startValue + progress * (endValue - startValue);
            setCount(currentValue);

            if (progress < 1) {
                timer = requestAnimationFrame(updateCounter);
            } else {
                setCount(endValue);
            }
        };

        if (inView) {
            timer = requestAnimationFrame(updateCounter);
        }

        return () => cancelAnimationFrame(timer);
    }, [end, duration, inView]);

    // Formatage selon le type de valeur
    const formattedValue = () => {
        if (typeof end === "string") {
            if (end.includes("%")) {
                return Math.round(count) + "%";
            }
            if (end.includes("x")) {
                return count.toFixed(1) + "x";
            }
            if (end.includes("+")) {
                return Math.round(count) + "+";
            }
            if (end.includes("s")) {
                return count.toFixed(1) + "s";
            }
        }
        return count.toString() + suffix;
    };

    return <div ref={inViewRef}>{formattedValue()}</div>;
};

const KeyMetrics = () => {
    const sectionRef = useRef(null);
    const [metricsRef, metricsInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // Use shared mouse parallax hook
    useMouseParallax({ elementRef: sectionRef });

    return (
        <section
            className="pb-20 pt-5 md:pt-40 relative overflow-hidden"
            ref={sectionRef}
            id="resultats"
        >
            {/* Floating elements - More subtle and modern */}
            <div className="absolute top-20 right-[20%] w-72 h-72 rounded-full bg-primary-500/5 blur-3xl floating-element"></div>
            <div className="absolute bottom-20 left-[15%] w-64 h-64 rounded-full bg-secondary-500/5 blur-3xl floating-element"></div>
            <div className="absolute top-1/2 left-[60%] w-48 h-48 rounded-full bg-yellow-400/5 blur-2xl floating-element"></div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center mb-16">
                    <motion.h2
                        className="text-4xl md:text-5xl font-bold text-center title mb-8"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        Nos <span className="text-gradient">résultats</span>{" "}
                        parlent d'eux-mêmes
                    </motion.h2>
                    {/* <div className="w-20 h-1 bg-primary-gradient mx-auto rounded-full"></div> */}
                </div>

                <div className="max-w-5xl mx-auto" ref={metricsRef}>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                        {metrics.map((metric, index) => (
                            <div
                                key={metric.id}
                                className={`
                                    bg-white rounded-xl shadow-lg shadow-gray-100/40 p-6 transform transition-all duration-500 border
                                    hover:shadow-xl hover:-translate-y-1 hover:bg-gray-50
                                    ${
                                        metricsInView
                                            ? "opacity-100 translate-y-0"
                                            : "opacity-0 translate-y-10"
                                    }
                                `}
                            >
                                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-primary-50 text-gradient mx-auto mb-4">
                                    {metric.icon}
                                </div>
                                <div className="font-heading text-4xl md:text-5xl font-bold mb-2 text-center">
                                    <CountUp end={metric.value} />
                                </div>
                                <div className="text-secondary-600 text-center font-medium">
                                    {metric.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default KeyMetrics;
